import React, { useState, useEffect, ChangeEvent } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./PublicCandidateSummary.css";

interface CandidateFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  location: string;
  linkedin_url: string;
  summary: string;
  tags: string;
  notice_period: string;
  english_level: string;
  expected_rate: string;
  [key: string]: string;
}

interface FormErrors {
  first_name?: string;
  last_name?: string;
  email?: string;
  expected_rate?: string;
  linkedin_url?: string;
  [key: string]: string | undefined;
}

interface Application {
  id: number;
  job_title?: string;
  stage_name?: string;
  stage_color?: string;
  department?: string;
  job_location?: string;
  status?: string;
  created_at: string;
  updated_at: string;
}

interface CandidatePublicProfile {
  candidate_code: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  location?: string;
  linkedin_url?: string;
  summary?: string;
  tags?: string[];
  notice_period?: string;
  english_level?: string;
  expected_rate?: number | string;
  role_type?: string;
  seniority?: string;
  main_skill?: string;
  applications?: Application[];
}

interface SaveMessage {
  type: "success" | "error";
  text: string;
}

const PublicCandidateSummary: React.FC = () => {
  const { t } = useTranslation("public");
  const { code } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [candidate, setCandidate] = useState<CandidatePublicProfile | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<CandidateFormData>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    location: "",
    linkedin_url: "",
    summary: "",
    tags: "",
    notice_period: "",
    english_level: "",
    expected_rate: "",
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<SaveMessage | null>(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError(
        "Access link required. Please use the share link provided by the recruiter.",
      );
      setErrorCode("TOKEN_REQUIRED");
      return;
    }

    const fetchCandidate = async () => {
      try {
        setLoading(true);
        setError(null);
        setErrorCode(null);
        const response = await fetch(
          `/api/recruiting/public/candidate/${code}?token=${encodeURIComponent(token)}`,
        );
        const data = await response.json();

        if (data.success) {
          setCandidate(data.data);
          setFormData({
            first_name: data.data.first_name || "",
            last_name: data.data.last_name || "",
            email: data.data.email || "",
            phone: data.data.phone || "",
            location: data.data.location || "",
            linkedin_url: data.data.linkedin_url || "",
            summary: data.data.summary || "",
            tags: Array.isArray(data.data.tags)
              ? data.data.tags.join(", ")
              : "",
            notice_period: data.data.notice_period || "",
            english_level: data.data.english_level || "",
            expected_rate: data.data.expected_rate || "",
          });
        } else {
          setErrorCode(data.code || null);
          setError(data.error || data.message || "Candidate not found");
        }
      } catch (err) {
        setError("Failed to load candidate information");
        console.error("Error fetching candidate:", err);
      } finally {
        setLoading(false);
      }
    };

    if (code) {
      fetchCandidate();
    }
  }, [code, token]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }

    // Clear save message when user starts editing
    if (saveMessage) {
      setSaveMessage(null);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.first_name?.trim()) {
      newErrors.first_name = t("candidateSummary.validation.firstNameRequired");
    }

    if (!formData.last_name?.trim()) {
      newErrors.last_name = t("candidateSummary.validation.lastNameRequired");
    }

    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (
      formData.expected_rate &&
      Number.isNaN(Number.parseFloat(formData.expected_rate))
    ) {
      newErrors.expected_rate = "Expected rate must be a number";
    }

    if (formData.linkedin_url && !formData.linkedin_url.startsWith("http")) {
      newErrors.linkedin_url =
        "LinkedIn URL must start with http:// or https://";
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);

    try {
      // Prepare data for API
      const updateData = {
        ...formData,
        expected_rate: formData.expected_rate
          ? Number.parseFloat(formData.expected_rate)
          : null,
        tags: formData.tags
          ? formData.tags
              .split(",")
              .map((tag) => tag.trim())
              .filter(Boolean)
          : [],
      };

      const response = await fetch(
        `/api/recruiting/public/candidate/${code}?token=${encodeURIComponent(token!)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updateData),
        },
      );

      const data = await response.json();

      if (data.success) {
        setCandidate(data.data);
        setIsEditing(false);
        setSaveMessage({
          type: "success",
          text: "Profile updated successfully!",
        });
        setTimeout(() => setSaveMessage(null), 5000);
      } else {
        setSaveMessage({
          type: "error",
          text: data.error || data.message || "Failed to update profile",
        });
      }
    } catch (err) {
      console.error("Error updating candidate:", err);
      setSaveMessage({
        type: "error",
        text: "Failed to update profile. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original candidate data
    if (candidate) {
      setFormData({
        first_name: candidate.first_name || "",
        last_name: candidate.last_name || "",
        email: candidate.email || "",
        phone: candidate.phone || "",
        location: candidate.location || "",
        linkedin_url: candidate.linkedin_url || "",
        summary: candidate.summary || "",
        tags: Array.isArray(candidate.tags) ? candidate.tags.join(", ") : "",
        notice_period: candidate.notice_period || "",
        english_level: candidate.english_level || "",
        expected_rate: String(candidate.expected_rate || ""),
      });
    }
    setIsEditing(false);
    setFormErrors({});
    setSaveMessage(null);
  };

  if (loading) {
    return (
      <div className="public-summary-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading candidate information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-summary-container">
        <div className="error-message">
          <h2>
            {errorCode === "LINK_EXPIRED" ? "Link Expired" : "Access Denied"}
          </h2>
          <p>{error}</p>
          {errorCode === "LINK_EXPIRED" ? (
            <p>Please contact the recruiter to receive a new share link.</p>
          ) : (
            <p>
              Please use the share link provided by your recruiter to access
              this page.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="public-summary-container">
        <div className="error-message">
          <h2>No Data Available</h2>
          <p>No candidate information found for this code.</p>
        </div>
      </div>
    );
  }

  const getStageBadgeStyle = (stageColor?: string): React.CSSProperties => {
    return {
      backgroundColor: stageColor || "#6c757d",
      color: "white",
      padding: "4px 12px",
      borderRadius: "20px",
      fontSize: "0.875rem",
      fontWeight: "500",
    };
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="public-summary-container">
      <div className="public-summary-header">
        <div className="company-logo">
          <img src="/hirefocuz-logo.png" alt="HireFocuZ" />
        </div>
        <h1>Candidate Profile</h1>
        <p className="candidate-code">Code: {candidate.candidate_code}</p>
      </div>

      {saveMessage && (
        <div className={`save-message ${saveMessage.type}`}>
          {saveMessage.text}
        </div>
      )}

      <div className="candidate-profile">
        <div className="candidate-info">
          <div className="candidate-header-section">
            <h2>
              {isEditing ? (
                <div className="edit-name-fields">
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className={`form-input ${formErrors.first_name ? "error" : ""}`}
                    placeholder="First Name"
                  />
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className={`form-input ${formErrors.last_name ? "error" : ""}`}
                    placeholder="Last Name"
                  />
                  {formErrors.first_name && (
                    <span className="error-text">{formErrors.first_name}</span>
                  )}
                  {formErrors.last_name && (
                    <span className="error-text">{formErrors.last_name}</span>
                  )}
                </div>
              ) : (
                `${candidate.first_name} ${candidate.last_name}`
              )}
            </h2>
            {!isEditing && (
              <button onClick={() => setIsEditing(true)} className="btn-edit">
                Edit Profile
              </button>
            )}
            {isEditing && (
              <div className="edit-actions">
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="btn-save"
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="btn-cancel"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div className="candidate-details">
            <div className="detail-item">
              <strong>Email:</strong>
              {isEditing ? (
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.email ? "error" : ""}`}
                  placeholder="Email"
                />
              ) : (
                <span>{candidate.email || "Not provided"}</span>
              )}
              {formErrors.email && (
                <span className="error-text">{formErrors.email}</span>
              )}
            </div>

            <div className="detail-item">
              <strong>Phone:</strong>
              {isEditing ? (
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Phone"
                />
              ) : (
                <span>{candidate.phone || "Not provided"}</span>
              )}
            </div>

            <div className="detail-item">
              <strong>Location:</strong>
              {isEditing ? (
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Location"
                />
              ) : (
                <span>{candidate.location || "Not provided"}</span>
              )}
            </div>

            <div className="detail-item">
              <strong>LinkedIn:</strong>
              {isEditing ? (
                <input
                  type="url"
                  name="linkedin_url"
                  value={formData.linkedin_url}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.linkedin_url ? "error" : ""}`}
                  placeholder="https://linkedin.com/in/..."
                />
              ) : (
                <span>
                  {candidate.linkedin_url ? (
                    <a
                      href={candidate.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Profile
                    </a>
                  ) : (
                    "Not provided"
                  )}
                </span>
              )}
              {formErrors.linkedin_url && (
                <span className="error-text">{formErrors.linkedin_url}</span>
              )}
            </div>

            <div className="detail-item">
              <strong>Role:</strong> {candidate.role_type || "Not specified"}
            </div>

            <div className="detail-item">
              <strong>Seniority:</strong>{" "}
              {candidate.seniority || "Not specified"}
            </div>

            <div className="detail-item">
              <strong>Main Skill:</strong>{" "}
              {candidate.main_skill || "Not specified"}
            </div>

            <div className="detail-item">
              <strong>Expected Rate:</strong>
              {isEditing ? (
                <input
                  type="number"
                  name="expected_rate"
                  value={formData.expected_rate}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.expected_rate ? "error" : ""}`}
                  placeholder="Rate per day"
                />
              ) : (
                <span>
                  {candidate.expected_rate
                    ? `$${candidate.expected_rate}/day`
                    : "Not specified"}
                </span>
              )}
              {formErrors.expected_rate && (
                <span className="error-text">{formErrors.expected_rate}</span>
              )}
            </div>

            <div className="detail-item">
              <strong>Notice Period:</strong>
              {isEditing ? (
                <input
                  type="text"
                  name="notice_period"
                  value={formData.notice_period}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="e.g., 2 weeks"
                />
              ) : (
                <span>{candidate.notice_period || "Not specified"}</span>
              )}
            </div>

            <div className="detail-item">
              <strong>English Level:</strong>
              {isEditing ? (
                <select
                  name="english_level"
                  value={formData.english_level}
                  onChange={handleInputChange}
                  className="form-input"
                >
                  <option value="">Select level</option>
                  <option value="Basic">Basic</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Native">Native</option>
                </select>
              ) : (
                <span>{candidate.english_level || "Not specified"}</span>
              )}
            </div>
          </div>

          <div className="candidate-summary-section">
            <h3>Summary</h3>
            {isEditing ? (
              <textarea
                name="summary"
                value={formData.summary}
                onChange={handleInputChange}
                className="form-textarea"
                placeholder="Professional summary..."
                rows={5}
              />
            ) : (
              <p>{candidate.summary || "No summary provided."}</p>
            )}
          </div>

          <div className="candidate-tags-section">
            <h3>Skills & Technologies</h3>
            {isEditing ? (
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Comma-separated tags (e.g., React, Node.js, Azure)"
              />
            ) : (
              <div className="tags-container">
                {candidate.tags && candidate.tags.length > 0 ? (
                  candidate.tags.map((tag) => (
                    <span key={tag} className="tag">
                      {tag}
                    </span>
                  ))
                ) : (
                  <p className="no-tags">No skills listed.</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="applications-section">
          <h3>Application Status</h3>
          {candidate.applications && candidate.applications.length > 0 ? (
            <div className="applications-list">
              {candidate.applications.map((application) => (
                <div key={application.id} className="application-card">
                  <div className="application-header">
                    <h4>{application.job_title || "Untitled Position"}</h4>
                    <span
                      className="stage-badge"
                      style={getStageBadgeStyle(application.stage_color)}
                    >
                      {application.stage_name || "Unknown Stage"}
                    </span>
                  </div>
                  <div className="application-details">
                    <div className="application-info">
                      {application.department && (
                        <span className="department">
                          {application.department}
                        </span>
                      )}
                      {application.job_location && (
                        <span className="location">
                          {" "}
                          {application.job_location}
                        </span>
                      )}
                    </div>
                    <div className="application-status">
                      <span
                        className={`status-badge status-${application.status?.toLowerCase() || "unknown"}`}
                      >
                        {application.status || "Unknown"}
                      </span>
                    </div>
                    <div className="application-dates">
                      <small>
                        Applied: {formatDate(application.created_at)}
                      </small>
                      {application.updated_at !== application.created_at && (
                        <small>
                          Last updated: {formatDate(application.updated_at)}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-applications">
              <p>No applications found for this candidate.</p>
            </div>
          )}
        </div>
      </div>

      <div className="public-summary-footer">
        <p>
          This is your candidate profile. You can update your information using
          the Edit Profile button above.
        </p>
        <p className="footer-note">
          Powered by <strong>Startup Master</strong> - Recruitment Management
          Platform
        </p>
      </div>
    </div>
  );
};

export default PublicCandidateSummary;
